import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ddtApi, companyApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Download, Pencil, Printer, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { printPdfBlob } from '../lib/printPdf';

const DDTViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';
  const [ddt, setDDT] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [ddtRes, companyRes] = await Promise.all([
        ddtApi.getById(id),
        companyApi.get()
      ]);
      setDDT(ddtRes.data);
      setCompany(companyRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento del DDT');
      navigate('/ddt');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await ddtApi.getPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DDT_${ddt.number}_${ddt.year}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handlePrint = async () => {
    // Stampa il PDF generato dal backend senza lasciare aperta una nuova scheda
    try {
      const response = await ddtApi.getPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (!printPdfBlob(blob)) toast.error('Abilita i popup per stampare');
    } catch (error) {
      toast.error('Errore nella stampa');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  if (!ddt) {
    return null;
  }

  return (
    <div data-testid="ddt-view-page" className="space-y-6">
      {/* Header - No print */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ddt')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">
              DDT n° {ddt.number}-{ddt.year}
            </h1>
            <p className="text-muted-foreground text-sm">
              {ddt.client_name} - {formatDate(ddt.date)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="print-ddt-btn">
            <Printer className="w-4 h-4 mr-2" />
            Stampa
          </Button>
          <Button variant="outline" data-testid="download-pdf-btn" onClick={handleDownloadPdf}>
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Link to={`/ddt/${id}/edit`}>
            <Button data-testid="edit-ddt-btn" className="btn-primary">
              <Pencil className="w-4 h-4 mr-2" />
              Modifica
            </Button>
          </Link>
        </div>
      </div>

      {/* Margin Card - ONLY ON SCREEN, NOT PRINT, MASTER ONLY */}
      {!isSlave && (
      <Card className="card-swiss bg-green-50/50 border-green-200/50 no-print">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-sm">
                <TrendingUp className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-green-700 font-medium">Guadagno su questo DDT</p>
                <p className="text-xs text-green-600">(Visibile solo a te, non nel PDF)</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold font-['JetBrains_Mono'] text-green-700">
                {formatCurrency(ddt.total_margin || 0)}
              </p>
              <p className="text-xs text-green-600">
                Costo: {formatCurrency(ddt.total_cost || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Document Preview */}
      <Card className="card-swiss max-w-4xl mx-auto">
        <CardContent className="p-8">
          {/* Header */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border-2 border-foreground p-4 bg-muted/10">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Mittente</p>
              <h2 className="text-2xl font-bold font-['Manrope'] tracking-tight">{company?.name}</h2>
              <p className="text-sm mt-1">{company?.address}</p>
              <p className="text-sm">{company?.cap} {company?.city}</p>
              <p className="text-sm font-['JetBrains_Mono'] mt-2">
                P.IVA {company?.piva} {company?.codice_sdi && `· SDI ${company.codice_sdi}`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{company?.email} · {company?.phone}</p>
            </div>
            <div className="border-2 border-foreground p-4">
              <h3 className="text-lg font-bold font-['Manrope']">DOCUMENTO DI TRASPORTO</h3>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                <span className="text-muted-foreground">N° doc:</span>
                <span className="font-['JetBrains_Mono'] font-bold text-right">{ddt.number}-{ddt.year}</span>
                <span className="text-muted-foreground">Data:</span>
                <span className="font-['JetBrains_Mono'] text-right">{formatDate(ddt.date)}</span>
                <span className="text-muted-foreground">Causale:</span>
                <span className="text-right">{ddt.causale || 'Vendita'}</span>
                <span className="text-muted-foreground">Porto:</span>
                <span className="text-right">{ddt.porto || 'Franco'}</span>
              </div>
            </div>
          </div>

          {/* Client */}
          <div className="border-2 border-foreground p-4 mb-6 bg-muted/5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Destinatario</p>
            <p className="text-xl font-bold font-['Manrope']">{ddt.client_name}</p>
            <p className="text-sm mt-1">{ddt.client_address}</p>
            <p className="text-sm">{ddt.client_cap} {ddt.client_city}</p>
            {ddt.client_piva && (
              <p className="text-sm font-['JetBrains_Mono'] mt-1">P. IVA {ddt.client_piva}</p>
            )}
          </div>

          {/* Internal Ref */}
          {ddt.internal_ref && (
            <div className="border-2 border-foreground p-3 mb-4 inline-block">
              <p className="text-sm"><b>RIF. INTERNO:</b> {ddt.internal_ref}</p>
            </div>
          )}

          {/* Items Table */}
          <Table className="mb-6">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-bold text-foreground border border-border">Codice</TableHead>
                <TableHead className="font-bold text-foreground border border-border">Descrizione</TableHead>
                <TableHead className="font-bold text-foreground border border-border text-center">Box</TableHead>
                <TableHead className="font-bold text-foreground border border-border text-center">Pezzi/Box</TableHead>
                <TableHead className="font-bold text-foreground border border-border text-center">Pezzi</TableHead>
                <TableHead className="font-bold text-foreground border border-border text-right">Prezzo</TableHead>
                <TableHead className="font-bold text-foreground border border-border text-right">Tot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ddt.items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="border border-border font-['JetBrains_Mono']">
                    {item.product_code}
                  </TableCell>
                  <TableCell className="border border-border">{item.description}</TableCell>
                  <TableCell className="border border-border text-center font-['JetBrains_Mono']">
                    {item.boxes}
                  </TableCell>
                  <TableCell className="border border-border text-center font-['JetBrains_Mono']">
                    {item.pieces_per_box}
                  </TableCell>
                  <TableCell className="border border-border text-center font-['JetBrains_Mono']">
                    {item.pieces}
                  </TableCell>
                  <TableCell className="border border-border text-right font-['JetBrains_Mono']">
                    {item.price?.toFixed(2)}
                  </TableCell>
                  <TableCell className="border border-border text-right font-['JetBrains_Mono']">
                    {item.total?.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="space-y-2 text-right">
              <p>
                <span className="text-muted-foreground mr-4">Box: {ddt.total_boxes}</span>
                <span className="text-muted-foreground">Pezzi: {ddt.total_pieces}</span>
              </p>
              <div className="flex justify-end items-center gap-4 pt-2">
                <span className="font-semibold">IMPONIBILE €</span>
                <span className="font-['JetBrains_Mono'] font-bold text-lg border-2 border-foreground px-4 py-1">
                  {ddt.total_amount?.toFixed(2)}
                </span>
              </div>
              {(ddt.previous_balance || 0) > 0 && (
                <>
                  <div data-testid="view-ddt-vecchio" className="flex justify-end items-center gap-4 pt-1">
                    <span className="font-semibold text-amber-700">+ VECCHIO €</span>
                    <span className="font-['JetBrains_Mono'] font-bold text-lg border-2 border-amber-500 bg-amber-50 px-4 py-1 text-amber-900">
                      {Number(ddt.previous_balance).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-end items-center gap-4 pt-1">
                    <span className="font-bold">TOTALE A PAGARE €</span>
                    <span className="font-['JetBrains_Mono'] font-bold text-xl border-2 border-foreground bg-foreground text-background px-4 py-1">
                      {((ddt.total_amount || 0) + (ddt.previous_balance || 0)).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
              {(ddt.paid_amount || 0) > 0 && (
                <div className="flex justify-end items-center gap-4 pt-1 text-sm">
                  <span className="text-muted-foreground">Acconto pagato</span>
                  <span className="font-['JetBrains_Mono'] text-green-700 font-semibold">€ {Number(ddt.paid_amount).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Footer Info */}
          <div className="grid grid-cols-4 gap-0 border border-border text-center text-sm mb-4">
            <div className="p-3 border-r border-border">
              <p className="text-muted-foreground text-xs mb-1">Causale del trasporto</p>
              <p className="font-medium">{ddt.causale}</p>
            </div>
            <div className="p-3 border-r border-border">
              <p className="text-muted-foreground text-xs mb-1">Aspetto dei beni</p>
              <p className="font-medium">{ddt.aspetto_beni}</p>
            </div>
            <div className="p-3 border-r border-border">
              <p className="text-muted-foreground text-xs mb-1">Data ed ora inizio trasporto</p>
              <p className="font-medium">{ddt.transport_date} {ddt.transport_time}</p>
            </div>
            <div className="p-3">
              <p className="text-muted-foreground text-xs mb-1">Firma Conducente</p>
              <p className="font-medium">&nbsp;</p>
            </div>
          </div>

          {/* Vettore */}
          {ddt.vettore && (
            <p className="text-sm mb-2">VETTORE: {ddt.vettore}</p>
          )}

          {/* Note */}
          {ddt.note && (
            <div className="border border-border p-3 mt-4">
              <p className="text-sm">N.B. {ddt.note}</p>
            </div>
          )}
          
          {/* Footer text */}
          <p className="text-xs text-center text-muted-foreground mt-6">
            Deposito e sede amministrativo
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DDTViewPage;
